import clsx from 'clsx'

import { Flex, ToggleGroup, Tooltip } from '@buffer-mono/popcorn'

import { formatDateRange } from '../../utils'
import type { SegmentedOption } from '../ResponsiveSegmentedControl'

import sharedStyles from '../chart-shared.module.css'
import { isViewMode, ViewMode } from '../MetricChart/types'

import type { JSX } from 'react'

interface PeriodToggleProps {
  value: ViewMode
  onChange: (value: ViewMode) => void
  /** Start of the primary period, shown in the "This Period" and "Both" tooltips. */
  startDate: Date
  /** End of the primary period, shown in the "This Period" and "Both" tooltips. */
  endDate: Date
  /** Start of the comparison period, shown in the "Comparison" and "Both" tooltips. */
  comparisonStartDate?: Date
  /** End of the comparison period, shown in the "Comparison" and "Both" tooltips. */
  comparisonEndDate?: Date
}

// This @types/react has no index signature for `--*` keys, so the marker
// colour custom property is modelled explicitly.
interface PeriodDotStyle extends React.CSSProperties {
  '--dot-color'?: string
}

// A colour marker matching the toggle items, for the collapsed dropdown options.
function PeriodDot({ dashed = false }: { dashed?: boolean }): JSX.Element {
  const dotStyle: PeriodDotStyle = { '--dot-color': 'currentColor' }
  return (
    <span
      className={clsx(
        sharedStyles.colorDot,
        dashed && sharedStyles.colorDotDashed,
      )}
      aria-hidden="true"
      style={dotStyle}
    />
  )
}

// Options for the collapsed (dropdown) form of the toggle, shown on narrow cards.
export const PERIOD_DROPDOWN_OPTIONS: SegmentedOption[] = [
  { value: ViewMode.Primary, label: 'This Period', icon: <PeriodDot /> },
  {
    value: ViewMode.Comparison,
    label: 'Comparison',
    icon: <PeriodDot dashed />,
  },
  { value: ViewMode.Both, label: 'Both' },
]

export function PeriodToggle({
  value,
  onChange,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
}: PeriodToggleProps): JSX.Element {
  const primaryRange = formatDateRange(startDate, endDate)
  const comparisonRange =
    comparisonStartDate && comparisonEndDate
      ? formatDateRange(comparisonStartDate, comparisonEndDate)
      : undefined

  // "Both" overlays the two periods, so its tooltip needs to label which range
  // is which. Falls back to the primary range alone if no comparison is set.
  const bothTooltip = comparisonRange ? (
    <Flex direction="column" gap="2xs" align="start">
      <span>This Period: {primaryRange}</span>
      <span>Comparison: {comparisonRange}</span>
    </Flex>
  ) : (
    primaryRange
  )

  const handleChange = (next: string): void => {
    if (isViewMode(next)) {
      onChange(next)
    }
  }

  return (
    <ToggleGroup
      type="single"
      size="medium"
      className={sharedStyles.insightsToggle}
      value={value}
      onChange={handleChange}
      aria-label="Chart view"
    >
      <PeriodToggleItem content={primaryRange}>
        <ToggleGroup.Item value={ViewMode.Primary} aria-label="This period">
          <Flex align="center" gap="2xs">
            <PeriodDot />
            This Period
          </Flex>
        </ToggleGroup.Item>
      </PeriodToggleItem>
      <PeriodToggleItem content={comparisonRange}>
        <ToggleGroup.Item
          value={ViewMode.Comparison}
          aria-label="Comparison period"
        >
          <Flex align="center" gap="2xs">
            <PeriodDot dashed />
            Comparison
          </Flex>
        </ToggleGroup.Item>
      </PeriodToggleItem>
      <PeriodToggleItem content={bothTooltip}>
        <ToggleGroup.Item value={ViewMode.Both} aria-label="Both periods">
          Both
        </ToggleGroup.Item>
      </PeriodToggleItem>
    </ToggleGroup>
  )
}

/**
 * Renders a toggle item with its date-range tooltip. The wrapper span absorbs
 * the Tooltip trigger's `data-state`, so it never overrides the ToggleGroup
 * item's own `data-state="on"` (which drives the selected styling). The tooltip
 * is omitted when there's no content, so a missing comparison range never
 * surfaces an empty tooltip on hover.
 */
function PeriodToggleItem({
  content,
  children,
}: {
  content: React.ReactNode
  children: React.ReactElement
}): JSX.Element {
  const trigger = (
    <span className={sharedStyles.toggleTooltipWrapper}>{children}</span>
  )
  if (!content) {
    return trigger
  }
  return <Tooltip content={content}>{trigger}</Tooltip>
}
